import { isTempAttach, parseCloudViewerUrl } from '@mail/cross-url-sharing';
import { captureException } from '@sentry/browser';
import { logger } from 'lib/logger';
import xray from 'lib/xray';
import { parse } from 'qs';
import { getMessage, getTemporaryMessageAttaches } from 'reactApp/api/axios.corsapi';
import * as api from 'reactApp/api/axios.corsapi';
import { attachesViewerSuccess, failure, request, success } from 'reactApp/modules/attaches/attaches.actions';
import { EAttachesLoad, EAttachesOpenViewer, EAttachesPageStart, EAttachesViewer } from 'reactApp/modules/attaches/attaches.constants';
import { getSidesMessages, hasMoreToLoad } from 'reactApp/modules/attaches/attaches.helpers';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { MemoryCache } from 'reactApp/modules/cache/MemoryCache';
import {
    getAttachesCacheTtlMs,
    getEmlIdRegExp,
    getFeatureAttachesIdRegExps,
    getFeatureMessagesIdRegExps,
} from 'reactApp/modules/features/features.selectors';
import { AttachFileBuilder } from 'reactApp/modules/file/AttachFileBuilder';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { setQueryParams } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { closeViewer, openViewer, updateViewerData } from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { sendKaktamLog, sendXray } from 'reactApp/utils/ga';
import { call, cancel, fork, put, select, takeEvery } from 'redux-saga/effects';

const cache = new MemoryCache();
const attachFileBuilder = new AttachFileBuilder();

let loadingMessages: string[] = [];

/*
     Подгружает для просмотрщика все аттачи данного письма с инфой о письме
 */
function* viewerAttachesLoad({ itemId, messageId }) {
    if (!itemId) {
        return;
    }

    try {
        if (messageId && !loadingMessages.includes(messageId)) {
            const { temporaryMessageIdReg } = yield select(getFeatureMessagesIdRegExps);
            const isTempAttachItem = isTempAttach(messageId, temporaryMessageIdReg);

            loadingMessages.push(messageId);
            const email = yield select(UserSelectors.getEmail);

            const folderId = parse(window.location.search, { ignoreQueryPrefix: true })['folder-id'];
            let res;

            if (isTempAttachItem) {
                res = yield call(getTemporaryMessageAttaches, { messageId, email });
            } else {
                // @ts-ignore
                res = yield call(getMessage, { messageId, email, folderId });
            }

            if (!res) {
                sendXray(['attaches', 'saga', 'nomessagefound']);
                sendKaktamLog(
                    { itemId, location: window.location, messageId, isTempAttachItem, text: 'no message from api for this message_id' },
                    'cloud_attaches_saga'
                );
                yield put(closeViewer());
                return;
            }

            let attaches = [];
            const list = isTempAttachItem ? res : res?.attaches?.list;

            if (Array.isArray(list)) {
                attaches = list
                    .map((item) => ({
                        ...item,
                        id: `${messageId};${item.id}`,
                        message_id: messageId,
                        storage: EStorageType.viewerAttaches,
                    }))
                    .reduce((acc, item): Record<string, AttachesItem> => {
                        return {
                            ...acc,
                            [item.id]: {
                                ...attachFileBuilder.prepareFile(
                                    isTempAttachItem
                                        ? item
                                        : {
                                              ...item,
                                              subject: res.subject,
                                              authorEmail: res.email,
                                              from: res.correspondents?.from,
                                              time: res.date,
                                          }
                                ),
                                attachType: isTempAttachItem ? EAttachTypes.temporary : item.type || EAttachTypes.attach,
                            },
                        };
                    }, {});
            }

            yield put(attachesViewerSuccess({ attaches, messageId }));

            loadingMessages = loadingMessages.filter((item) => item !== messageId);

            return attaches;
        }
    } catch (error) {
        logger.error(error);
        captureException(error);
        yield cancel();
    }
}

function* handleViewerAttachesLoadMore(action) {
    const { itemId, ids } = action.payload;

    if (!itemId || !ids) {
        return;
    }

    try {
        const { before, after } = getSidesMessages(itemId, ids);

        const loadedMsgIds = yield select(AttachesSelectors.getLoadedMessagesIds);

        if (before && !loadedMsgIds.includes(before.messageId)) {
            yield fork(viewerAttachesLoad, { messageId: before.messageId, itemId: before.itemId });
        }
        if (after && !loadedMsgIds.includes(after.messageId)) {
            yield fork(viewerAttachesLoad, { messageId: after.messageId, itemId: after.itemId });
        }
    } catch (error) {
        logger.error(error);
        captureException(error);
        yield cancel();
    }
}

function* handleLoadMessageAttachesAndOpenViewer(id: string, keepPreviousData = false) {
    const attachesRegExps = yield select(getFeatureAttachesIdRegExps);
    const messageIdsRegExps = yield select(getFeatureMessagesIdRegExps);
    const emlFormatId = yield select(getEmlIdRegExp);

    const regExps = {
        attachesRegExps,
        messageIdsRegExps,
        emlFormatId,
    };

    // Парсим урл
    const { result, error } = parseCloudViewerUrl(id, regExps);

    let messageId, encodedAttachId;
    if (error) {
        const parsedResult = yield select(getCurrentRouteId);
        messageId = parsedResult.messageId;
        encodedAttachId = parsedResult.partId;

        /** Посылаем радар о неуспешном парсинге урла только когда есть messageId, так как наличие
         error может означать также что это урл 'https://cloud.mail.ru/attaches/?{get params}'
         не на просмотр конкретного аттача.
         Чтобы определить, что был фейл был при парсинге именно урла на облачный просмотрщик с аттачом,
         можно проверять messageId (аналогичная проверка в handleAttachesPageStart в attaches.saga.ts) */
        if (messageId) {
            xray.send('parse_viewer_url_fail', {
                i: error,
                rlog: 'parse_viewer_url_fail',
                rlog_message: {
                    project: 'cloud.mail.ru',
                    url: id,
                    error,
                },
            });
        }
    } else {
        messageId = result?.messageId;
        encodedAttachId = result?.encodedAttachId;

        xray.send('parse_viewer_url_ok');
    }

    // Normalize messageId/attachId to form 'messageId;attachId'.
    const initialId = messageId ? `${messageId};${encodedAttachId}` : '';

    try {
        if (!initialId) {
            return;
        }

        const { fromCloud } = yield select(SettingsSelectors.getQueryParams);

        if (messageId) {
            const viewerData = {
                itemId: initialId,
                itemStorage: EStorageType.viewerAttaches,
                gaSuffix: '',
                itemIds: [],
                fromCloud,
            };
            yield put(openViewer(viewerData));

            const attaches = yield viewerAttachesLoad({ itemId: initialId, messageId });

            if (!attaches) {
                yield put(closeViewer());
                return;
            }

            yield put(updateViewerData({ ...viewerData, keepPreviousData }));
        }
    } catch (error) {
        sendXray(['attaches', 'saga', 'startexception']);
        sendKaktamLog({ initialId, text: 'exception for the id' }, 'cloud_attaches_saga');
        logger.error(error);
        captureException(error);
        yield cancel();
    }
}

function* handleAttachesPageStart(action) {
    yield call(handleLoadMessageAttachesAndOpenViewer, window.location.href);
}

/*
    Подгружает список аттачей для даталиста и догружает до текущего аттача в просм-ке, если он открыт.
    Также отвечает за дозагрузку аттачей при скролле.
 */
function* handleAttachesLoad(action) {
    const { ...params } = action.payload;

    try {
        const { fromCloud } = yield select(SettingsSelectors.getQueryParams);

        yield put(request(params));

        const ttl = yield select(getAttachesCacheTtlMs);
        const key = `getattaches${JSON.stringify(params)}`;
        const response = yield call(cache.getOrAdd, key, () => api.getAttaches(params), ttl);

        const attaches =
            response?.attaches && Array.isArray(response.attaches)
                ? response.attaches.reduce(
                      (acc, item): Record<string, AttachesItem> => ({
                          ...acc,
                          [item.id]: {
                              ...attachFileBuilder.prepareFile(item),
                          },
                      }),
                      {}
                  )
                : {};
        yield put(success({ attaches, hasMoreToLoad: hasMoreToLoad(response?.attaches), after: response?.after }));

        const initialItemId = yield select(ViewerSelectors.getViewerItemId);
        
        if (fromCloud && initialItemId) {
            const isLoadViewerFile = !!response?.attaches.find((item) => item.id === initialItemId);
            if (isLoadViewerFile || !response?.after) {
                yield put(
                    updateViewerData({
                        itemId: initialItemId,
                        itemStorage: EStorageType.viewerAttaches,
                        gaSuffix: '',
                        itemIds: [],
                        fromCloud,
                    })
                );
                yield fork(handleViewerAttachesLoadMore, { payload: { itemId: initialItemId, ids: Object.keys(attaches) } });
            } else if (response?.after) {
                yield fork(handleAttachesLoad, { payload: { ...params, after: response.after } });
            }
        }
    } catch (error) {
        logger.error(error);
        captureException(error);
        yield put(failure(error));
        yield cancel();
    }
}

// При открытии аттача из даталиста, нужно сходить в ручку для дополнения описания всех аттачей этого письма (например, урлы редактирования доков).
function* handleAttachesOpenViewer(action) {
    const id = `${window.location.origin}/attaches/${encodeURIComponent(action.payload.id)}`;

    yield put(setQueryParams({ fromCloud: true }));

    yield call(handleLoadMessageAttachesAndOpenViewer, id, true);

    // Догружаем аттачи из примыкающих слева+справа писем в тор аттачей просмотрщика
    const itemId = yield select(ViewerSelectors.getViewerItemId);
    const ids = yield select(ViewerSelectors.getViewerItemIds);

    yield call(handleViewerAttachesLoadMore, { payload: { itemId, ids } });
}

export function* watchAttchesRoot() {
    yield takeEvery(EAttachesViewer.LOADMORE.toString(), handleViewerAttachesLoadMore);
    yield takeEvery(EAttachesPageStart.REQUEST.toString(), handleAttachesPageStart);
    yield takeEvery(EAttachesLoad.REQUEST.toString(), handleAttachesLoad);
    yield takeEvery(EAttachesOpenViewer.REQUEST.toString(), handleAttachesOpenViewer);
}
